import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/textfield/outlined-text-field.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";

import { pb, logout } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { inputValue, errorMessage } from "../lib/dom";
import { sharedStyles, authStyles } from "../styles/shared";

/** Internal operator sign-in. This console (Prospecting & Onboarding) is for
 * platform operators only — any other account is rejected after auth. */
@customElement("login-page")
export class LoginPage extends LitElement {
  static styles = [sharedStyles, authStyles];

  @state() private email = "";
  @state() private password = "";
  @state() private loading = false;
  @state() private error = "";
  @state() private resetSent = false;

  /** The confirm page is served by the tenant portal on the public host (the
   * emailed link goes there); this console only needs to start the flow. */
  private async handleForgot(): Promise<void> {
    this.error = "";
    if (!this.email) {
      this.error = "Enter your email first, then request the reset.";
      return;
    }
    this.loading = true;
    try {
      await pb.collection("tenancy_users").requestPasswordReset(this.email);
      this.resetSent = true;
    } catch (err) {
      this.error = errorMessage(err, "Could not request a reset.");
    } finally {
      this.loading = false;
    }
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    this.error = "";
    this.loading = true;
    try {
      await pb
        .collection("tenancy_users")
        .authWithPassword(this.email, this.password);
      const roles = (pb.authStore.record?.roles as string[] | undefined) ?? [];
      if (!roles.includes("platform_operator")) {
        logout();
        this.error = "This console is for platform operators only.";
        return;
      }
      await navigate("/counterfeit");
    } catch (err) {
      this.error = errorMessage(
        err,
        "Could not sign in. Check your credentials.",
      );
    } finally {
      this.loading = false;
    }
  }

  render() {
    return html`
      <div class="auth-page">
        <div class="auth-card">
          <h1>Operator sign in</h1>
          <p class="subtitle">Authentrace — Prospecting &amp; Onboarding.</p>
          <form @submit=${(e: Event) => void this.handleSubmit(e)}>
            <md-outlined-text-field
              label="Email"
              type="email"
              autocomplete="email"
              required
              .value=${this.email}
              @input=${(e: Event) => (this.email = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Password"
              type="password"
              autocomplete="current-password"
              required
              .value=${this.password}
              @input=${(e: Event) => (this.password = inputValue(e))}
            ></md-outlined-text-field>
            ${this.error
              ? html`<p class="error-text">${this.error}</p>`
              : null}
            <md-filled-button type="submit" ?disabled=${this.loading}>
              ${this.loading ? "Signing in…" : "Sign in"}
            </md-filled-button>
            ${this.resetSent
              ? html`<p class="subtitle">
                  If that address has an account, a reset email is on its way.
                </p>`
              : html`<md-text-button
                  type="button"
                  ?disabled=${this.loading}
                  @click=${() => void this.handleForgot()}
                >
                  Forgot your password?
                </md-text-button>`}
          </form>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "login-page": LoginPage;
  }
}
